import React, {useEffect, useState} from 'react';
import {Tooltip} from 'react-tooltip';
import MapHelper from "../../../../helpers/MapHelper";
import ShapeStyling from "../../shapes/ShapeStyling";
import ImportFromFilesModal from "./ImportFromFilesModal";
import {useAuth} from "../../../../providers/AuthProvider";

import L from 'leaflet';
import 'proj4leaflet';
import proj4 from 'proj4';
import {useNotifications} from "../../../../providers/NotificationsProvider";


export default function ImportExportControl({shapes, marks, project, projectId, onShapesImported, onImportFromFiles, canEdit = false}) {
    const {user} = useAuth();
    const notifications = useNotifications();

    const [showImportFromFilesModal, setShowImportFromFilesModal] = React.useState(false);
    const [projectStatus, setProjectStatus] = useState(project.status ? project.status : '30_cancelled');


    useEffect(() => {
        if (project.status) {
            setProjectStatus(project.status);
        }
    }, [project.status]);

    const onExportClick = () => {
        let geojsonData = {};
        if (shapes) {
            const features = Object.values(shapes).map(element => element.geoJSON);
            features.forEach((feature, i) => {
                if (feature.properties) {
                    feature.properties.shapeType = 'selection';
                } else {
                    feature.properties = {shapeType: 'selection'};
                }

                features[i] = feature;
            });
            const featuresMarks = Object.values(marks).map(element => element.geoJSON);
            featuresMarks.forEach((feature, i) => {
                if (feature.properties) {
                    feature.properties.shapeType = 'reference';
                } else {
                    feature.properties = {shapeType: 'reference'};
                }

                featuresMarks[i] = feature;
            });
            features.push(...featuresMarks);

            geojsonData = {
                type: "FeatureCollection",
                features: features,
                properties: {
                    title: project.title,
                    description: project.description
                }
            };
        }

        const dataStr = JSON.stringify(geojsonData);
        const blob = new Blob([dataStr], {type: 'application/json'});
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'geoData-'+projectId+'.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }


    const onImportClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,.geojson';
        input.onchange = e => {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = JSON.parse(e.target.result);
                parseImportedFile(data);
            }
            reader.readAsText(file);
        }
        input.click();
    }


    const parseImportedFile = (data) => {
        let newShapeDrafts = {...shapes};
        if (data.type !== 'FeatureCollection' || !data.features) {
            data = {
                features: [data]
            };
        }

        let crs = null;
        if (data.crs && data.crs.properties && data.crs.properties.name) {
            if (data.crs.properties.name.indexOf(':27700') !== -1) {
                crs = 'EPSG:27700';
            }
        }

        if (data.features && data.features.length > 30) {
            notifications.notify('Too many shapes in the file. Only the first 30 will be imported.', 'warning');

            data.features = data.features.slice(0, 30);
        }

        let totalCoordinates = 0;
        data.features.forEach((feature, index) => {
            const type = MapHelper.detectShapeType(feature);

            if (type === 'MultiPolygon' && feature.geometry && feature.geometry.coordinates && feature.geometry.coordinates.length) {
                feature.geometry.coordinates.forEach((polygonCoordinates, pIndex) => {
                    const id = ShapeStyling.generateRandomId();
                    let polygonFeature = {
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: polygonCoordinates
                        },
                        properties: feature.properties ? {...feature.properties} : {}
                    };

                    if (crs === 'EPSG:27700') {
                        polygonFeature = convert27700ToWGS84(polygonFeature);
                        feature.coordinates[pIndex] = polygonFeature.geometry.coordinates;
                        polygonCoordinates = polygonFeature.geometry.coordinates;
                        data.features[index] = feature;
                    }

                    totalCoordinates += calculateCoordinates(polygonCoordinates);

                    if (!polygonFeature.properties || !polygonFeature.properties.color) {
                        const newColor = ShapeStyling.generateRandomColor();
                        /*data.features[index].properties = {
                            ...polygonFeature.properties,
                            color: newColor
                        };*/
                        polygonFeature.properties = {
                            ...polygonFeature.properties,
                            color: newColor
                        };
                    }

                    newShapeDrafts[id] = {
                        id: id,
                        active: false,
                        bounds: L.geoJSON(polygonFeature).getBounds(),
                        type: 'Polygon',
                        geoJSON: polygonFeature,
                        layerData: {}
                    };
                });
            } else if (type && type !== 'MultiPolygon') {
                const id = ShapeStyling.generateRandomId();

                if (crs === 'EPSG:27700') {
                    feature = convert27700ToWGS84(feature);
                    data.features[index] = feature;
                }

                totalCoordinates += calculateCoordinates(feature.geometry.coordinates);

                if (!feature.properties || !feature.properties.color) {
                    const newColor = ShapeStyling.generateRandomColor();
                    data.features[index].properties = {
                        ...feature.properties,
                        color: newColor
                    };
                    feature.properties = {
                        ...feature.properties,
                        color: newColor
                    };
                }

                newShapeDrafts[id] = {
                    id: id,
                    active: false,
                    bounds: L.geoJSON(feature).getBounds(),
                    type: type,
                    geoJSON: feature,
                    layerData: {}
                };
            } else {
                notifications.notify('Couldn\'t identify shape type: '+(feature.geometry.type ? feature.geometry.type : 'Undefined'), 'warning');

                return;
            }
        });

        if (totalCoordinates > 5000) {
            notifications.notify('Too many coordinates in the file. Please reduce the number of coordinates to 5,000 or less.', 'warning');

            return;
        }

        const bounds = MapHelper.getGeoJSONBounds(data);

        onShapesImported(newShapeDrafts, bounds);
    }


    const calculateCoordinates = (coordinates) => {
        if (Array.isArray(coordinates[0][0])) {
            let total = 0;

            coordinates.forEach(coord => {
                total += calculateCoordinates(coord);
            });

            return total;
        } else {
            return coordinates.length;
        }
    }


    const convert27700ToWGS84 = (geojson) => {
        const from = 'EPSG:27700';
        const to = 'EPSG:4326';

        proj4.defs(from, '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs');

        const [x, y] = geojson.geometry.coordinates;
        const [lon, lat] = proj4(from, to, [x, y]);

        return {
            ...geojson,
            geometry: {
                type: 'Point',
                coordinates: [lon, lat]
            }
        };
    };


    const onImportFromFilesClick = () => {
        setShowImportFromFilesModal(true);
    }


    const onImportFromFilesCancel = () => {
        setShowImportFromFilesModal(false);
    }


    const onImportFromFilesSubmit = (fileId) => {
        setShowImportFromFilesModal(false);
        onImportFromFiles(fileId);
    }


    return (
        <>
            <div className={'btn-group-vertical'}>
                <button
                    onClick={onExportClick}
                    className={'btn btn-onmap '+((shapes && Object.keys(shapes).length || marks && Object.keys(marks).length) ? '' : 'd-none')+(user.type !== 'admin' ? ' d-none' : '')}

                    data-tooltip-content="Export shapes to GeoJSON file"
                    data-tooltip-place="right"
                    data-tooltip-id="import-export-tooltip"
                >
                    <i className={'ti ti-package-export fs-5'}></i>
                </button>

                <button
                    onClick={onImportClick}
                    className={'btn btn-onmap'+((projectStatus !== '00_in_progress' || !canEdit) ? ' d-none' : '')}
                    data-tooltip-content="Import shapes from GeoJSON file"
                    data-tooltip-place="right"
                    data-tooltip-id="import-export-tooltip"
                >
                    <i className={'ti ti-package-import fs-5'}></i>
                </button>

                <button
                    onClick={onImportFromFilesClick}
                    className={'btn btn-onmap'+((projectStatus !== '00_in_progress' || !canEdit) ? ' d-none' : '')}
                    data-tooltip-content="Import from My Files"
                    data-tooltip-place="right"
                    data-tooltip-id="import-export-tooltip"
                >
                    <i className={'ti ti-folder-down fs-5'}></i>
                </button>

                <Tooltip id="import-export-tooltip"/>
            </div>


            <ImportFromFilesModal
                visible={showImportFromFilesModal}
                onCancel={onImportFromFilesCancel}
                onClick={onImportFromFilesSubmit}
            />
        </>
    );
}